import { FC } from 'react';
import styled from 'styled-components';
import { CorporateTheme } from '../../utils/themes';
import LeftArrow from '../../assets/icons/LeftArrow';
import RightArrow from '../../assets/icons/RightArrow';
import { useTheme } from '../../middleware/ThemeContext';

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  perPage: number;
  totalPages: number;
  className?:string;
  hidePageSizeSelector?: boolean;
  onPageChange: (page: number) => void;
  onLimitChange: (perPage: number) => void;
}

const PaginationContainer = styled('div')(({ theme }) => ({
  paddingTop: CorporateTheme.spacing.sm,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: theme.CorporateTheme.colors.background,
  width: '100%',
}));

const LeftControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${CorporateTheme.spacing.md};
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${CorporateTheme.spacing.xs};
`;

const PageButton = styled('button')<{ $active?: boolean }>(
  ({ theme, $active }) => ({
    padding: `${CorporateTheme.spacing.xs} ${CorporateTheme.spacing.sm}`,
    minWidth: '28px',
    minHeight: '28px',
    fontSize: '14px',
    fontFamily: "'Lato', sans-serif",
    fontWeight: $active ? 700 : 500,
    border: 'none',
    borderRadius: CorporateTheme.borderRadius,
    color: theme.CorporateTheme.colors.paginationSelect,
    cursor: 'pointer',
    transition: 'all 0.2s',
    background: $active ? '#63636326' : 'transparent',

    '&:hover:not(:disabled)': {
      borderColor: theme.CorporateTheme.colors.accent,
      color: theme.CorporateTheme.colors.accent,
    },

    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  })
);

const PageSelect = styled('select')(({ theme }) => ({
  border: 'none',
  fontSize: '14px',
  fontFamily: "'Lato', sans-serif",
  color: theme.CorporateTheme.colors.paginationSelect,
  backgroundColor: theme.colors.contentBackground,
}));

const PaginationText = styled('div')(({ theme }) => ({
  fontSize: '13px',
  fontFamily: "'Lato', sans-serif",
  color: theme.CorporateTheme.colors.paginationSelect,
}));

const Pagination: FC<PaginationProps> = ({
  currentPage,
  totalItems,
  perPage,
  totalPages,
  onPageChange,
  onLimitChange,
  className,
  hidePageSizeSelector = false,
}) => {
  const { theme } = useTheme();
  const getPageRange = () => {
    const current = currentPage;
    const total = totalPages;
    const range: (number | string)[] = [];
    const maxVisible = 5;

    if (total <= maxVisible) return Array.from({ length: total }, (_, i) => i + 1);

    let start = Math.max(2, current - 1);
    let end = Math.min(total - 1, current + 1);

    if (current - 1 <= 2) end = maxVisible - 2;
    if (total - current <= 2) start = total - (maxVisible - 2);

    range.push(1);
    if (start > 2) range.push('...');
    for (let i = start; i <= end; i++) range.push(i);
    if (end < total - 1) range.push('...');
    range.push(total);

    return range;
  };

  const start = (currentPage - 1) * perPage + 1;
  const end = Math.min(currentPage * perPage, totalItems);

  return (
    <PaginationContainer theme={theme} className={className}>
      <LeftControls>
        {!hidePageSizeSelector && (
          <PageSelect
            theme={theme}
            value={perPage}
            onChange={(e) => onLimitChange(Number(e.target.value))}
          >
            {[10, 20, 30, 50].map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </PageSelect>
        )}
        <PaginationText theme={theme}>
          Showing {start} to {end} of {totalItems} entries
        </PaginationText>
      </LeftControls>

      <RightControls>
            <PageButton
              theme={theme}
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <LeftArrow color={theme.CorporateTheme.colors.textSecondary} />
            </PageButton>

            {getPageRange().map((page, index) => (
              typeof page === 'number' ? (
                <PageButton
                  theme={theme}
                  key={page}
                  $active={page === currentPage}
                  onClick={() => onPageChange(page)}
                >
                  {page}
                </PageButton>
              ) : (
                <span key={`ellipsis-${index}`}>...</span>
              )
            ))}

            <PageButton
              theme={theme}
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <RightArrow color={theme.CorporateTheme.colors.textSecondary} />
            </PageButton>
      </RightControls>
    </PaginationContainer>
  );
};

export default Pagination;